import React, { useState } from "react";
import {
  User,
  Mail,
  Calendar,
  Link as LinkIcon,
  Unlink,
  AlertTriangle,
  Trash2,
  Download,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { API_BASE_URL } from "../../config/api";

const AccountSettings = ({ user, onLogout }) => {
  const { linkGoogleAccount, unlinkGoogleAccount, initiateGoogleAuth } =
    useAuth();
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [accountSuccess, setAccountSuccess] = useState("");

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteCode, setDeleteCode] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Data export state
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  // Parse OAuth providers
  let oauthProviders = [];
  try {
    oauthProviders = JSON.parse(user?.oauthProviders || "[]");
  } catch (e) {
    oauthProviders = [];
  }

  const hasGoogleLinked = oauthProviders.includes("google");
  const hasPassword = !!user?.passwordHash;

  const handleLinkGoogle = async () => {
    try {
      setAccountLoading(true);
      setAccountError("");
      setAccountSuccess("");
      await initiateGoogleAuth();
      // User will be redirected
    } catch (err) {
      setAccountError(err.message || "Failed to link Google account");
      setAccountLoading(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    if (!hasPassword) {
      setAccountError(
        "Cannot unlink Google: You must set a password first to maintain account access",
      );
      return;
    }

    if (
      !window.confirm("Are you sure you want to unlink your Google account?")
    ) {
      return;
    }

    try {
      setAccountLoading(true);
      setAccountError("");
      setAccountSuccess("");
      await unlinkGoogleAccount();
      setAccountSuccess("Google account unlinked successfully");
    } catch (err) {
      setAccountError(err.message || "Failed to unlink Google account");
    } finally {
      setAccountLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleting(true);
    setDeleteError("");
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${API_BASE_URL}/delete-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          userId: user.id,
          password: deletePassword || undefined,
          code: deleteCode || undefined,
          confirmation: deleteConfirmation,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to delete account");
      }
      // Clear local data and log out
      try {
        localStorage.clear();
      } catch (_) {}
      if (onLogout) onLogout();
      window.location.href = "/";
    } catch (err) {
      setDeleteError(err.message || "Failed to delete account");
      setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeletePassword("");
    setDeleteCode("");
    setDeleteConfirmation("");
    setDeleteError("");
  };

  const handleDownloadData = async () => {
    setExporting(true);
    setExportError("");
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${API_BASE_URL}/export-account-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ userId: user.id }),
      });
      if (!response.ok) {
        let message = "Failed to export account data";
        try {
          const errBody = await response.json();
          message = errBody.error || message;
        } catch (_) {}
        throw new Error(message);
      }
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lyrical-toolkit-export-${user.id}-${today}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message || "Failed to export account data");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Account Info */}
      <div>
        <h3 className="text-xl font-semibold mb-5 text-[#d0eadb]">
          Account Information
        </h3>

        <div className="space-y-4">
          {/* Username */}
          <div className="p-5 rounded-lg bg-[#1e3a28]">
            <div className="flex items-center gap-3 mb-3">
              <User className="w-6 h-6 text-[#7ab892]" />
              <span className="text-base font-medium text-[#a5d4b8]">
                Username
              </span>
            </div>
            <p className="text-lg font-medium text-[#d0eadb]">
              {user?.username || "N/A"}
            </p>
          </div>

          {/* Email */}
          <div className="p-5 rounded-lg bg-[#1e3a28]">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-6 h-6 text-[#7ab892]" />
              <span className="text-base font-medium text-[#a5d4b8]">
                Email
              </span>
            </div>
            <p className="text-lg font-medium text-[#d0eadb]">
              {user?.email || "N/A"}
            </p>
          </div>

          {/* Member Since */}
          <div className="p-5 rounded-lg bg-[#1e3a28]">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="w-6 h-6 text-[#7ab892]" />
              <span className="text-base font-medium text-[#a5d4b8]">
                Member Since
              </span>
            </div>
            <p className="text-lg font-medium text-[#d0eadb]">
              {user?.createdAt ? formatDate(user.createdAt) : "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Connected Accounts Section */}
      <div>
        <h3 className="text-xl font-semibold mb-5 text-[#d0eadb]">
          Connected Accounts
        </h3>

        {accountError && (
          <div className="mb-4 p-4 rounded-lg text-base bg-[#c45a2d]/20 text-[#d0eadb]">
            {accountError}
          </div>
        )}

        {accountSuccess && (
          <div className="mb-4 p-4 rounded-lg text-base bg-[#3d7a5a]/20 text-[#d0eadb]">
            {accountSuccess}
          </div>
        )}

        <div className="space-y-4">
          {/* Google Account */}
          <div className="p-5 rounded-lg bg-[#1e3a28]">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                {/* Google Logo */}
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g fill="none" fillRule="evenodd">
                    <path
                      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                      fill="#4285F4"
                    />
                    <path
                      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
                      fill="#34A853"
                    />
                    <path
                      d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                      fill="#EA4335"
                    />
                  </g>
                </svg>
                <div>
                  <p className="text-base font-medium text-[#d0eadb]">Google</p>
                  <p className="text-sm text-[#7ab892]">
                    {hasGoogleLinked ? (
                      <>Connected: {user?.google_email || user?.email}</>
                    ) : (
                      "Not connected"
                    )}
                  </p>
                </div>
              </div>

              {hasGoogleLinked ? (
                <button
                  onClick={handleUnlinkGoogle}
                  disabled={accountLoading}
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors bg-[#c45a2d] hover:bg-[#c45a2d]/80 text-[#0d1a14] ${
                    accountLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <Unlink className="w-4 h-4" />
                  Unlink
                </button>
              ) : (
                <button
                  onClick={handleLinkGoogle}
                  disabled={accountLoading}
                  className={`py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors bg-[#c5a34e] hover:bg-[#c5a34e]/80 text-[#0d1a14] ${
                    accountLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <LinkIcon className="w-4 h-4" />
                  Link Account
                </button>
              )}
            </div>
          </div>

          {/* Info about password requirement */}
          {hasGoogleLinked && !hasPassword && (
            <div className="p-4 rounded-lg text-base bg-[#c5a34e]/20 text-[#d0eadb]">
              <p className="font-medium mb-1">
                Set a password to enable unlinking
              </p>
              <p>
                To unlink your Google account, you must first set a password to
                maintain access to your account.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div>
        <h3 className="text-xl font-semibold mb-5 text-[#d0eadb]">
          Danger Zone
        </h3>

        {/* Download my data — safe action, neutral gold styling */}
        <div className="p-5 rounded-lg border border-[#c5a34e]/40 bg-[#c5a34e]/10 mb-4">
          <div className="flex items-start gap-3 mb-4">
            <Download className="w-6 h-6 text-[#c5a34e] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-base font-medium text-[#d0eadb] mb-1">
                Download my data
              </p>
              <p className="text-sm text-[#a5d4b8]">
                Export everything we hold about you — profile, songs, version
                history, recording metadata, projects, tags, subscription
                summary, and recent audit log — as a single JSON file.
              </p>
            </div>
          </div>
          {exportError && (
            <div className="mb-3 p-3 rounded-lg bg-[#c45a2d]/20 text-[#d0eadb] text-sm">
              {exportError}
            </div>
          )}
          <button
            onClick={handleDownloadData}
            disabled={exporting}
            className="py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors bg-[#c5a34e] hover:bg-[#c5a34e]/80 text-[#0d1a14] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Preparing…" : "Download my data"}
          </button>
        </div>

        {/* Delete account — destructive action */}
        <div className="p-5 rounded-lg border border-[#c45a2d]/40 bg-[#c45a2d]/10">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-[#c45a2d] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-base font-medium text-[#d0eadb] mb-1">
                Delete account
              </p>
              <p className="text-sm text-[#a5d4b8]">
                Permanently delete your account, all songs, recordings,
                uploaded audio, projects, and tags. Active subscriptions will
                be canceled. This cannot be undone.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="py-2 px-4 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors bg-[#c45a2d] hover:bg-[#c45a2d]/80 text-[#0d1a14]"
          >
            <Trash2 className="w-4 h-4" />
            Delete my account
          </button>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={closeDeleteModal}
        >
          <div
            className="w-full max-w-md rounded-lg bg-[#1e3a28] border border-[#c45a2d]/40 p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#c45a2d]" />
              <h4 className="text-lg font-semibold text-[#d0eadb]">
                Delete account
              </h4>
            </div>

            <p className="text-sm text-[#a5d4b8] mb-4">
              This will immediately and permanently remove your account and
              all of your data. We cannot recover it for you afterward.
            </p>

            <form onSubmit={handleDeleteAccount} className="space-y-4">
              {hasPassword && (
                <div>
                  <label className="block text-sm font-medium text-[#a5d4b8] mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="w-full px-3 py-2 rounded-lg bg-[#0d1a14] border border-[#2a5038] text-[#d0eadb] focus:outline-none focus:border-[#c5a34e]"
                  />
                </div>
              )}

              {user?.twoFactorEnabled && (
                <div>
                  <label className="block text-sm font-medium text-[#a5d4b8] mb-1">
                    2FA code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={deleteCode}
                    onChange={(e) => setDeleteCode(e.target.value)}
                    autoComplete="one-time-code"
                    placeholder="123456"
                    required
                    className="w-full px-3 py-2 rounded-lg bg-[#0d1a14] border border-[#2a5038] text-[#d0eadb] focus:outline-none focus:border-[#c5a34e]"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#a5d4b8] mb-1">
                  Type <span className="font-mono text-[#c45a2d]">DELETE</span>{" "}
                  to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-lg bg-[#0d1a14] border border-[#2a5038] text-[#d0eadb] focus:outline-none focus:border-[#c5a34e]"
                />
              </div>

              {deleteError && (
                <div className="p-3 rounded-lg bg-[#c45a2d]/20 text-[#d0eadb] text-sm">
                  {deleteError}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="py-2 px-4 rounded-lg text-sm font-medium bg-[#2a5038] hover:bg-[#2a5038]/80 text-[#d0eadb] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deleting || deleteConfirmation !== "DELETE"}
                  className="py-2 px-4 rounded-lg text-sm font-medium bg-[#c45a2d] hover:bg-[#c45a2d]/80 text-[#0d1a14] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleting ? (
                    "Deleting…"
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Permanently delete
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
